import { IEntity } from "@/types/breeze/Entity";
import SelectField, { SelectFieldProps } from "./SelectField";
import { useEntities } from "@/hooks/useCommonQueries";
import { Text } from "@radix-ui/themes";
import { useMemo } from "react";

interface EntitySelectProps extends Omit<SelectFieldProps, "type"> {
  valuePropName?: keyof IEntity;
  onEntitySelected?: (entity: IEntity) => void;
}

export default function EntitySelect(props: EntitySelectProps) {
  const { onEntitySelected, valuePropName = "id", ...rest } = props;
  const { data: entities, isLoading: isLoadingEntities } = useEntities();

  // Determine the type of the valuePropName property
  const fieldType = useMemo(() => {
    if (!entities || entities.length === 0) return "string";

    const firstEntity = entities[0];
    const value = firstEntity[valuePropName];

    if (typeof value === "number") {
      return "number";
    } else if (typeof value === "boolean") {
      return "string"; // Use string for boolean values
    } else {
      return "string"; // Default to string for strings and other types
    }
  }, [entities, valuePropName]);

  if (isLoadingEntities) {
    return <Text>Loading entities...</Text>;
  }

  return (
    <SelectField
      {...rest}
      type={fieldType}
      onChange={(e) => {
        // Convert the value to the appropriate type for comparison
        let compareValue: any = e;
        if (fieldType === "number") {
          compareValue = Number(e);
        }

        const entity = entities?.find(
          (entity) => entity[valuePropName] === compareValue,
        );
        if (entity) {
          onEntitySelected?.(entity);
        }
        props.onChange?.(e);
      }}
    >
      {entities?.map((entity) => (
        <SelectField.Item key={entity.id} value={entity[valuePropName] as any}>
          {entity.name}
        </SelectField.Item>
      ))}
    </SelectField>
  );
}
